import { random } from 'lodash';
import Head from 'next/head';
import React, { useState } from 'react';
import { Box, Container } from '@mui/material';
import BaseLayout from 'layouts/BaseLayout';
import Header from 'components/Templates/Header';
import List from 'components/Templates/List';
import AboutSection from 'components/Templates/AboutSection';
import FormatsService from 'services/FormatsService';
import TemplatesService from 'services/TemplatesService';
import { COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import useIsMobile from 'hooks/useIsMobile';
import { getEnv } from 'utils';
import { useRouter } from 'next/router';
import { setLocale } from 'utils/locale';

import en from '../../../config/locales/en';
import ja from '../../../config/locales/en';
import zh from '../../../config/locales/en';
import es from '../../../config/locales/es';
import fr from '../../../config/locales/fr';
import it from '../../../config/locales/it';

function getFormatListData(slug) {
  const data = {
    slides: {
      columns: 5,
      perPage: 15,
    },
    abstracts: {
      columns: 3,
      perPage: 9,
    },
    infographics: {
      columns: 6,
      perPage: 18,
    },
    posters: {
      columns: 5,
      perPage: 15,
    },
  };

  if (data[slug]) {
    return data[slug];
  }

  throw new Error(`Unable to get format list data for unknown format [${slug}].`);
}

export function getStaticPaths() {
  return { paths: [], fallback: 'blocking' };
}

export async function getStaticProps({ params: { slug } }) {
  try {
    const { data: format } = await FormatsService.fetchOneBySlug(slug);
    const { data: templates } = await TemplatesService.fetchAll({
      formatId: format.id,
      page: 1,
      perPage: getFormatListData(format.slug).perPage,
    });
    const { data: neutralTemplates } = await TemplatesService.fetchAll({
      formatId: format.id,
      page: 1,
      perPage: getFormatListData(format.slug).columns,
      neutral: true,
    });

    return {
      props: {
        format,
        templates,
        neutralTemplates,
      },
      revalidate: 604800, // 1 week
    };
  } catch (e) {
    console.error(e);

    return {
      props: {
        format: null,
        templates: {},
        neutralTemplates: {},
      },
      revalidate: 604800, // 1 week
    };
  }
}

export default function TemplatesFormatPage({
  format,
  templates: initialTemplates,
  neutralTemplates: initialNeutralTemplates,
}) {
  const [templates, setTemplates] = useState(initialTemplates);
  const [neutralTemplates, setNeutralTemplates] = useState(initialNeutralTemplates);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();
  const router = useRouter();

  function getNeutralTemplatesRandomPage() {
    const pagesTotal = Math.ceil(neutralTemplates.total / neutralTemplates.perPage);
    const pagesArray = Array.from({ length: pagesTotal }, (item, index) => index + 1);
    const possiblePagesArray = pagesArray.filter((page) => page !== neutralTemplates.page);

    if (possiblePagesArray.length) {
      return possiblePagesArray[random(0, possiblePagesArray.length - 1)];
    }

    return 1;
  }

  async function fetchTemplates(page) {
    try {
      const { data } = await TemplatesService.fetchAll({
        formatId: format.id,
        page,
        perPage: templates.perPage,
      });

      setTemplates(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchNeutralTemplates() {
    try {
      const { data } = await TemplatesService.fetchAll({
        formatId: format.id,
        page: getNeutralTemplatesRandomPage(),
        perPage: neutralTemplates.perPage,
        neutral: true,
      });

      setNeutralTemplates(data);
    } catch (e) {
      console.error(e);
    }
  }

  function handlePaginationChange(page) {
    if (page !== currentPage) {
      setCurrentPage(page);

      fetchTemplates(page);
      fetchNeutralTemplates();
    }
  }

  const { locale } = router;
  const isInfographicTemplatesPage = router.asPath.includes('/infographic-templates');
  const t = isInfographicTemplatesPage
    ? setLocale(locale, { en, es, fr, it, ja, zh })
    : setLocale('en', { en });

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <Head>
        <title>
          {t?.template_page_title?.[format?.name]
            ? `${t.template_page_title[format.name]} | Mind the Graph`
            : `${format?.name || 'Default'} Template | Mind the Graph`}
        </title>

        <meta
          key="og:description"
          name="description"
          content={
            t?.template_page_description?.[format?.name]
              ? t.template_page_description[format.name]
              : `${format?.name || 'Default'} template - Create professional scientific illustrations with Mind the Graph`
          }
        />
      </Head>

      <Header />

      <Box py={6} bgcolor="common.white">
        <Container>
          <List
            breadcrumbItems={[
              {
                label: 'Home',
                path: getEnv('MTG_URL_APP'),
              },
              {
                label: 'Templates',
                path: '/infographic-templates',
              },
              {
                label: format.name,
                path: router.asPath,
              },
            ]}
            formatName={format.name}
            templates={templates}
            columns={getFormatListData(format.slug).columns}
            currentPage={currentPage}
            onPaginationChange={handlePaginationChange}
            showH1Heading={format.name}
            showH1Description={
              'Browse our ' +
              format.name +
              ' created by scientists for scientists. Customize your perfect ' +
              format.name +
              ' in minutes, then download and share your impactful research visualization instantly.'
            }
          />

          {!!neutralTemplates.data.length && (
            <Box mt={4}>
              <List
                breadcrumbItems={[{ label: 'Neutral Templates' }]}
                formatName={`Neutral ${format.name}`}
                templates={neutralTemplates}
                columns={getFormatListData(format.slug).columns}
                expandButtonUrl={`/infographic-templates/category/neutral/format/${format.slug}`}
                showBreadcrumbs={false}
              />
            </Box>
          )}
        </Container>
      </Box>

      <AboutSection />
    </BaseLayout>
  );
}
